import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import { Icon } from '@/components/Icon/Icon';

import styles from './DownloadMenu.module.css';

export interface DownloadItem {
  label: string;
  href: string;
}

export interface DownloadMenuProps {
  items: DownloadItem[];
  /** The accessible name of the trigger. */
  label?: string;
}

/** A download icon opening a menu of the files a job produced. */
export function DownloadMenu({ items, label = 'Downloads' }: DownloadMenuProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const links = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const index = links.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      trigger.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      links[(index + step + links.length) % links.length]?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.root} ref={root}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}
      >
        <Icon name="download" size="1.15rem" />
      </button>
      {open ? (
        <div
          ref={menu}
          id={menuId}
          role="menu"
          aria-label={label}
          className={styles.menu}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => (
            <a
              key={item.label}
              role="menuitem"
              tabIndex={-1}
              className={styles.item}
              href={item.href}
              target="_blank"
              rel="noopener"
              onClick={() => setOpen(false)}
            >
              {item.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}
